import * as React from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { PiCheckCircle, PiInfo, PiWarningCircle, PiX, PiXCircle } from 'react-icons/pi'
import './redesigns.css'

/**
 * The asset frontend's own toast stack - the customer-facing counterpart to
 * `custolink-newdesign/ui/toast.tsx`'s `notify`. That one is painted with the
 * admin's `cl:`/`--cl-*` tokens and Geist; this one is painted with the
 * asset-frontend's `--rda-*` tokens, Schibsted and the Phosphor icon family,
 * so a customer who gets a toast on a sign-in or a custom-field form sees the
 * same product as the page under it, not the admin's design bleeding through.
 *
 * SELF-MOUNTING, same idiom as the admin toast and the old
 * `components/ui/toast/toast.tsx` it is replacing here: the first
 * `rdaNotify.*` call creates its own root on `document.body`. No `<Toaster>`
 * placement is needed, which matters because `useFormServerBacked` and
 * `useFormWrapper` reach pages outside any asset-frontend shell (the
 * `end_user` `*Design` pages, gated by `useAssetFrontendDesign()` rather than
 * being nested under `AssetShell`).
 *
 * Toasts render outside `AssetShell`/`AssetDrawerScope`, so - same reasoning
 * as `RdaDrawer` - only the fixed half of the palette is in scope here
 * (surface, ink, hairline), each with a literal fallback matching
 * `tileKit.tsx`'s `NEUTRALS`. The organisation's accent is deliberately not
 * used for the status colours: a toast must read as success/error/warning on
 * every organisation, including ones whose accent is itself red or green.
 */

export type RdaToastVariant = 'success' | 'danger' | 'warning' | 'info'

export interface RdaToastOptions {
  description?: React.ReactNode
  /** ms before auto-dismiss. 0 disables auto-dismiss. */
  duration?: number
}

interface RdaToastRecord {
  id: string
  variant: RdaToastVariant
  title: React.ReactNode
  description?: React.ReactNode
}

type Listener = () => void

let toasts: RdaToastRecord[] = []
const listeners = new Set<Listener>()

function emit() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  return toasts
}

let seq = 0
function nextId() {
  seq += 1
  return `rda-toast-${seq}`
}

const DEFAULT_DURATION = 4500

function dismiss(id: string) {
  const next = toasts.filter((t) => t.id !== id)
  if (next.length !== toasts.length) {
    toasts = next
    emit()
  }
}

function dismissAll() {
  if (toasts.length === 0) return
  toasts = []
  emit()
}

function push(variant: RdaToastVariant, title: React.ReactNode, options?: RdaToastOptions) {
  ensureMounted()

  const id = nextId()
  const duration = options?.duration ?? DEFAULT_DURATION
  toasts = [...toasts, { id, variant, title, description: options?.description }]
  emit()

  if (duration > 0) {
    setTimeout(() => dismiss(id), duration)
  }

  return id
}

/** Drop-in replacement for the old `toast.push(<Notification .../>)` call sites on the asset frontend. */
export const rdaNotify = {
  success: (title: React.ReactNode, description?: React.ReactNode) =>
    push('success', title, { description }),
  error: (title: React.ReactNode, description?: React.ReactNode) =>
    push('danger', title, { description }),
  warning: (title: React.ReactNode, description?: React.ReactNode) =>
    push('warning', title, { description }),
  info: (title: React.ReactNode, description?: React.ReactNode) =>
    push('info', title, { description }),
  dismiss,
  dismissAll,
}

const VARIANT_ICON: Record<RdaToastVariant, typeof PiCheckCircle> = {
  success: PiCheckCircle,
  danger: PiXCircle,
  warning: PiWarningCircle,
  info: PiInfo,
}

// Fixed, accent-independent status colours - see the file header for why.
const VARIANT_ICON_CLASS: Record<RdaToastVariant, string> = {
  success: 'text-emerald-600',
  danger: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-sky-600',
}

function RdaToastItem({ toast }: { toast: RdaToastRecord }) {
  const Icon = VARIANT_ICON[toast.variant]

  return (
    <div
      role="status"
      data-variant={toast.variant}
      className="rda-font-schibsted rda-toast pointer-events-auto flex w-full items-start gap-3 rounded-2xl border p-4 shadow-lg"
    >
      <Icon aria-hidden className={'mt-0.5 size-5 shrink-0 ' + VARIANT_ICON_CLASS[toast.variant]} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-sm font-semibold leading-snug">{toast.title}</p>
        {toast.description ? (
          <p className="rda-toast-muted text-sm leading-snug">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        className="rda-toast-muted shrink-0 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current"
        onClick={() => dismiss(toast.id)}
      >
        <PiX aria-hidden className="size-4" />
      </button>
    </div>
  )
}

function RdaToastViewport() {
  const items = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  return (
    <div
      data-slot="rda-toast-viewport"
      aria-live="polite"
      aria-atomic="false"
      className="rda-toast-viewport pointer-events-none fixed left-1/2 top-4 flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4"
    >
      {items.map((t) => (
        <RdaToastItem key={t.id} toast={t} />
      ))}
    </div>
  )
}

let toastRoot: Root | null = null

/** Lazily mounts the single toast root on `document.body`, once per page load. */
function ensureMounted() {
  if (toastRoot || typeof document === 'undefined') return

  const el = document.createElement('div')
  el.setAttribute('id', 'rda-toast-root')
  document.body.appendChild(el)

  toastRoot = createRoot(el)
  toastRoot.render(<RdaToastViewport />)
}
